import { v4 as uuid } from 'uuid';
import { imageKit } from '@meitu/wasm-lib';
import { getFramework, ResourceThumbnail } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { base64Kit } from '@/utils/base64-kit';
import { glKit } from '@/utils/gl-kit';
import { convertWebpToImage } from '@/utils/image-resource-kit';
import { computerImageTransform } from '@/utils/layer-kit';
import { getLayerOutputDraft, setLayerOutputSourceCanvas } from '@/utils/layer-output-draft-kit';
import {
    LayerType,
    type IExportBehavior,
    type IImageLayerProps,
    type IImageProps,
    type ILayerBehavior,
    type ILayerProps,
    type IMainLayerProps,
    type ISize,
} from '@/version-types';
import { EdgelessMainLayer } from './edgeless-layer';
import { renderMainLayerCanvas, renderMainLayerToStage } from './render/render-to-canvas';

function getBehavior(identity: string) {
    const layerBehavior: ILayerBehavior = {
        getEdgelessObject(layer: ILayerProps) {
            return new EdgelessMainLayer(layer.id, getFramework(identity));
        },

        onBeforeAddOrUpdate(layer: ILayerProps) {
            const data = layer.data as IImageProps;
            data.url = base64Kit.getCode(data.url);
        },
        async onBeforeOutputAsync(layer: ILayerProps) {
            const framework = getFramework(identity);
            framework.service.registration
                .getInstance<{ projectOutputDraft: (outputLayer: ILayerProps) => boolean }>('ImageFilterPlugin')
                ?.projectOutputDraft(layer);
            if (getLayerOutputDraft(layer)?.sourceCanvas) return;

            const runtimeLayer = framework.service.flatLayer.getLayer(layer.id);
            if (!runtimeLayer || !glKit.isUseGL(runtimeLayer)) return;
            const canvas = imageKit.getGLCanvas() as HTMLCanvasElement | null;
            if (canvas) setLayerOutputSourceCanvas(layer, canvas);
        },
        async onBeforeSaveAsync(layer: ILayerProps, _isAutoSave?: boolean) {
            const data = layer.data as IMainLayerProps;

            data.url = convertWebpToImage(data.url);
            data.maskURL = convertWebpToImage(data.maskURL);
            data.cutoutMaskURL = convertWebpToImage(data.cutoutMaskURL);

            const outputDraft = getLayerOutputDraft(layer);
            const outputCanvas = outputDraft?.sourceCanvas;
            if (outputCanvas) {
                data.url = base64Kit.getCode(outputCanvas.toDataURL('image/png'), uuid());
            } else {
                // 兼容未经过 ResultService 输出投影而直接调用保存行为的旧链路。
                const runtimeLayer = getFramework(identity).service.flatLayer.getLayer(layer.id);
                if (!runtimeLayer || !glKit.isUseGL(runtimeLayer)) {
                    if (data.url) data.url = await base64Kit.getRemoteUrlAsync(data.url);
                    return;
                }
                const canvas = await this.getResultCanvasAsync(runtimeLayer, runtimeLayer);
                if (canvas) {
                    const base64 = canvas.toDataURL();
                    data.url = base64Kit.getCode(base64, uuid());
                }
            }

            if (data.url) {
                data.url = await base64Kit.getRemoteUrlAsync(data.url);
            }
        },
        async getResultCanvasAsync(layer: ILayerProps, stageSize: ISize): Promise<HTMLCanvasElement | undefined> {
            const outputDraft = getLayerOutputDraft(layer);
            if (outputDraft?.sourceCanvas) {
                const layerCanvas = renderMainLayerCanvas({
                    layer,
                    baseImage: outputDraft.sourceCanvas,
                    forceRerender: true,
                });
                return renderMainLayerToStage(layer, layerCanvas, stageSize);
            }
            const obj = getFramework(identity).service.edgelessCanvas.getByLayerId(layer.id) as
                | EdgelessMainLayer
                | undefined;
            if (!obj) return;
            const layerCanvas = await obj.getCanvasResult({ forceRerender: true });
            return renderMainLayerToStage(layer, layerCanvas, stageSize);
        },
        checkDisableCollision(): boolean {
            return true;
        },
        checkDisableInGroup(): boolean {
            return true;
        },
        checkDisableFlip() {
            return true;
        },
        isMainLayer() {
            return true;
        },
        adjustResize(layer: ILayerProps, newWidth: number, newHeight) {
            const data = layer.data as IImageLayerProps;

            layer.height = newHeight;
            layer.width = newWidth;

            if (!data.url) return;

            computerImageTransform(newWidth, newHeight, data);
        },
        checkDisableActive(): boolean {
            return true;
        },

        getMenuOverlapName(layer: ILayerProps) {
            const { $t } = getLocaleStore();
            return $t((v) => v.plugin.hierarchy.mainLayer);
        },

        getMenuOverlapIcon(layer: ILayerProps) {
            const resource = getFramework(identity).service.edgelessCanvas.getByLayerId(layer.id)?.resource;
            const { url } = layer.data as IMainLayerProps;
            return <ResourceThumbnail resource={resource} fallbackUrl={base64Kit.getLocalUrl(url)} />;
        },
    };
    return layerBehavior;
}

const exportBehavior: IExportBehavior = {
    layerType: LayerType.MAIN_LAYER,
    getBehavior,
};

export default exportBehavior;
